import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import {
  FileText, Plus, Loader2, Save, Settings2, ListChecks, ShieldCheck,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import ManualPublishPanel from "@/components/websites/publishing/ManualPublishPanel";
import AutoPublishConfigPanel from "@/components/websites/publishing/AutoPublishConfigPanel";
import FrontPagePinCard from "@/components/websites/FrontPagePinCard";
import ShowcaseCard from "@/components/websites/ShowcaseCard";

const NICHE_OPTIONS = [
  "Tech & AI", "Cyber Security", "Programming", "Economy & Trade",
  "Corporate & Brands", "Entrepreneurship", "Politics & World",
  "Science", "Sports", "Lifestyle & Society", "Space & Aero", "Healthcare",
];

const SUB_CATEGORIES: Record<string, string[]> = {
  "Tech & AI": ["Artificial Intelligence", "Machine Learning", "Robotics", "Hardware"],
  "Cyber Security": ["Hacking", "Data Protection", "Privacy", "Threat Intel"],
  "Programming": ["JavaScript", "Python", "DevOps", "Cloud"],
  "Economy & Trade": ["Stock Market", "Crypto", "Global Trade", "Banking"],
  "Corporate & Brands": ["Startups", "Mergers", "Leadership", "Marketing"],
  "Entrepreneurship": ["Funding", "Growth", "Strategy", "Innovation"],
  "Politics & World": ["Diplomacy", "Elections", "Conflict", "Policy"],
  "Science": ["Physics", "Biology", "Space", "Climate"],
  "Sports": ["Football", "Basketball", "Tennis", "eSports"],
  "Lifestyle & Society": ["Culture", "Health", "Education", "Travel"],
  "Space & Aero": ["NASA", "SpaceX", "Satellites", "Aviation"],
  "Healthcare": ["Medicine", "Pharma", "Mental Health", "Biotech"],
};

const TONE_OPTIONS = ["Professional", "Tabloid", "Academic", "Conversational", "Investigative"];
const LANG_OPTIONS = [
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
  { value: "ar", label: "العربية" },
];

type ContentConfig = {
  niche?: string;
  subCategories?: string[];
  keywords?: string[];
  language?: string;
  tone?: string;
  autoReview?: boolean;
  // EU AI Act Art. 50 — "always" (default) appends the AI disclosure to every
  // published article; "exempt" suppresses it under the human-review exemption.
  aiDisclosure?: "always" | "exempt";
  aiAttestationAt?: string | null;
};

type Props = { siteId: string; siteName: string; domain?: string | null };

export default function ContentTab({ siteId, siteName, domain }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [keywordInput, setKeywordInput] = useState("");

  const { data: website } = useQuery({
    queryKey: ["website-detail", siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("websites")
        .select("content_config")
        .eq("id", siteId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const [config, setConfig] = useState<ContentConfig>({});

  useEffect(() => {
    if (website?.content_config) {
      setConfig(website.content_config as ContentConfig);
    }
  }, [website]);

  const updateConfig = (patch: Partial<ContentConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  };

  const saveStrategy = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("websites")
        .update({ content_config: config as any })
        .eq("id", siteId);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["website-detail", siteId] });
      toast.success("Content strategy saved");
      if (domain) {
        try {
          const { buildDeployPayload } = await import("@/lib/deployHelper");
          const payload = await buildDeployPayload(siteId, domain);
          await supabase.functions.invoke("plesk-provision", {
            body: { ...payload, sync_only: true },
          });
        } catch { /* best-effort */ }
      }
    } catch {
      toast.error("Failed to save strategy");
    } finally {
      setSaving(false);
    }
  };

  const addKeyword = () => {
    const kw = keywordInput.trim().toLowerCase();
    if (!kw) return;
    const existing = config.keywords || [];
    if (!existing.includes(kw)) updateConfig({ keywords: [...existing, kw] });
    setKeywordInput("");
  };

  const removeKeyword = (kw: string) => {
    updateConfig({ keywords: (config.keywords || []).filter((k) => k !== kw) });
  };

  const toggleSub = (sub: string) => {
    const existing = config.subCategories || [];
    if (existing.includes(sub)) updateConfig({ subCategories: existing.filter((s) => s !== sub) });
    else updateConfig({ subCategories: [...existing, sub] });
  };

  // Site-bound articles list (for Strategy quick view)
  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["site-articles", siteId],
    queryFn: async () => {
      const { data: pubs } = await supabase
        .from("article_publications")
        .select("article_id, status, published_at")
        .eq("website_id", siteId)
        .limit(50);
      if (!pubs || pubs.length === 0) {
        const { data, error } = await supabase
          .from("articles")
          .select("id, title, status, slug, published_at, updated_at")
          .eq("website_id", siteId)
          .order("updated_at", { ascending: false })
          .limit(20);
        if (error) throw error;
        return data || [];
      }
      const articleIds = pubs.map((p: any) => p.article_id);
      const { data, error } = await supabase
        .from("articles")
        .select("id, title, status, slug, published_at, updated_at")
        .in("id", articleIds)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const handleQuickCreate = async () => {
    if (!user) return;
    setCreating(true);
    try {
      const slug = `draft-${Date.now()}`;
      // NOTE: company_id is set by the `set_articles_default_company_id` BEFORE-INSERT
      // trigger (20260513120100). The cast works around STALE generated types
      // (types.ts marks company_id required on insert). HUMAN-ACTION: regenerate
      // Supabase types and drop this cast. See REMEDIATION-REPORT.md.
      const { data, error } = await (supabase as any)
        .from("articles")
        .insert({
          title: `New Article for ${siteName}`,
          slug,
          author_id: user.id,
          status: "approved",
          tags: config.keywords || [],
        })
        .select("id")
        .single();
      if (error) throw error;
      await supabase.from("article_publications").insert({
        article_id: data.id,
        website_id: siteId,
        status: "approved",
      });
      toast.success("Article created");
      navigate(`/articles/${data.id}`);
    } catch {
      toast.error("Failed to create article");
    } finally {
      setCreating(false);
    }
  };

  const statusColor: Record<string, string> = {
    draft: "bg-muted text-muted-foreground",
    review: "bg-blue-500/15 text-blue-600",
    approved: "bg-green-500/15 text-green-600",
    published: "bg-primary/15 text-primary",
    scheduled: "bg-yellow-500/15 text-yellow-600",
    archived: "bg-muted text-muted-foreground",
  };

  const availableSubs = config.niche ? SUB_CATEGORIES[config.niche] || [] : [];

  return (
    <Tabs defaultValue="manual" className="space-y-4">
      <TabsList className="grid w-full grid-cols-2 max-w-md">
        <TabsTrigger value="manual" className="text-xs gap-1.5">
          <ListChecks className="h-3.5 w-3.5" /> Manual Publish
        </TabsTrigger>
        <TabsTrigger value="automation" className="text-xs gap-1.5">
          <Settings2 className="h-3.5 w-3.5" /> Automation
        </TabsTrigger>
      </TabsList>

      {/* Manual */}
      <TabsContent value="manual" className="mt-0 space-y-4">
        <FrontPagePinCard siteId={siteId} />
        <ShowcaseCard siteId={siteId} />
        <ManualPublishPanel currentSiteId={siteId} currentSiteName={siteName} />
      </TabsContent>

      <TabsContent value="automation" className="mt-0 space-y-4">
        <AutoPublishConfigPanel siteId={siteId} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Strategy form */}
          <div className="rounded-lg border border-border p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Settings2 className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Automation Strategy</h3>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Primary niche</Label>
              <Select value={config.niche || ""} onValueChange={(v) => updateConfig({ niche: v, subCategories: [] })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select niche…" /></SelectTrigger>
                <SelectContent>
                  {NICHE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={n} className="text-xs">{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {availableSubs.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Sub-categories</Label>
                <div className="flex flex-wrap gap-1.5">
                  {availableSubs.map((sub) => {
                    const active = (config.subCategories || []).includes(sub);
                    return (
                      <button
                        key={sub}
                        onClick={() => toggleSub(sub)}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors border ${
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-muted text-muted-foreground border-border hover:bg-accent"
                        }`}
                      >
                        {sub}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Keywords / tags</Label>
              <div className="flex gap-1.5">
                <Input
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                  placeholder="Add keyword…"
                  className="h-7 text-xs flex-1"
                />
                <button onClick={addKeyword} className="rounded-md bg-accent px-2 text-xs font-medium hover:bg-accent/80">+</button>
              </div>
              {(config.keywords || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {(config.keywords || []).map((kw) => (
                    <span key={kw} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px]">
                      {kw}
                      <button onClick={() => removeKeyword(kw)} className="hover:text-destructive">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Language</Label>
                <Select value={config.language || "en"} onValueChange={(v) => updateConfig({ language: v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANG_OPTIONS.map((l) => (
                      <SelectItem key={l.value} value={l.value} className="text-xs">{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tone of voice</Label>
                <Select value={config.tone || "Professional"} onValueChange={(v) => updateConfig({ tone: v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TONE_OPTIONS.map((t) => (
                      <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border border-border p-2.5">
              <div>
                <p className="text-[11px] font-medium text-foreground">Manual review</p>
                <p className="text-[10px] text-muted-foreground">Auto-articles saved as drafts for approval</p>
              </div>
              <Switch
                checked={config.autoReview ?? true}
                onCheckedChange={(v) => updateConfig({ autoReview: v })}
              />
            </div>

            {/* EU AI Act Art. 50 — AI-generation disclosure on published articles.
                On by default; disabling requires attesting human editorial review. */}
            <div className="rounded-md border border-border p-2.5 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-medium text-foreground flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3 text-primary" /> AI Act disclosure
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    Appends an “AI-generated, editorially reviewed” notice + machine-readable marker to every published article (EU AI Act Art. 50).
                  </p>
                </div>
                <Switch
                  checked={(config.aiDisclosure ?? "always") !== "exempt"}
                  onCheckedChange={(on) =>
                    updateConfig({
                      aiDisclosure: on ? "always" : "exempt",
                      aiAttestationAt: on ? null : new Date().toISOString(),
                    })
                  }
                />
              </div>
              {config.aiDisclosure === "exempt" && (
                <p className="rounded bg-amber-500/10 border border-amber-500/30 px-2 py-1.5 text-[10px] leading-relaxed text-amber-700 dark:text-amber-400">
                  Disclosure off. You attest that every article on this site is reviewed by a person who holds editorial responsibility (the Art. 50(4) exemption). You remain liable for that attestation.
                  {config.aiAttestationAt && (
                    <span className="block mt-0.5 opacity-70">
                      Attested {formatDistanceToNow(new Date(config.aiAttestationAt), { addSuffix: true })}.
                    </span>
                  )}
                </p>
              )}
            </div>

            <button
              onClick={saveStrategy}
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save Automation
            </button>
          </div>

          {/* Linked articles */}
          <div className="rounded-lg border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">Articles on this site ({articles.length})</h3>
              </div>
              <button
                onClick={handleQuickCreate}
                disabled={creating}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                Quick Create
              </button>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : articles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <FileText className="h-6 w-6 text-muted-foreground mb-1" />
                <p className="text-xs text-muted-foreground">No articles linked yet</p>
              </div>
            ) : (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {articles.map((article) => (
                  <button
                    key={article.id}
                    onClick={() => navigate(`/articles/${article.id}`)}
                    className="w-full flex items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{article.title}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(article.updated_at), { addSuffix: true })}
                      </p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-medium uppercase ${statusColor[article.status] || statusColor.draft}`}>
                      {article.status}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}
